import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import Establishment from "../../models/establishment";
import DispatchPoint from "../../models/dispatch-point";
import Stamp from "../../models/stamp";
import { setTaxPayer } from "../tax-payer/tax-payer-slice";
import { LOG_OUT } from "../reducers/user";

export interface TaxPayerParametersState {
  loaded: boolean;
  loading: boolean;
  error: boolean;
  errorDetails: string[];
  taxPayerId?: number;
  data: {
    establishments: Establishment[];
    dispatchPoints: DispatchPoint[];
    stamps: Stamp[];
    connectivityAccess: boolean | null;
    accountingAccess: boolean | null;
    aiAccess: boolean | null;
    aiEnabled: boolean | null;
    aiSettingsManageable: boolean;
    isDemo: boolean | null;
    whatsappEnabled: boolean;
  };
}

const initialState: TaxPayerParametersState = {
  loaded: false,
  loading: false,
  error: false,
  errorDetails: [],
  data: {
    establishments: [],
    dispatchPoints: [],
    stamps: [],
    connectivityAccess: null,
    accountingAccess: null,
    aiAccess: null,
    aiEnabled: null,
    aiSettingsManageable: false,
    isDemo: null,
    whatsappEnabled: false,
  },
};

export const taxPayerParametersSlice = createSlice({
  name: "tax-payer-parameters",
  initialState,
  reducers: {
    setTaxPayerParametersLoaded: (state, action: PayloadAction<boolean>) => {
      state.loaded = action.payload;
    },
    setTaxPayerParametersLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setEstablishments: (state, action: PayloadAction<Establishment[]>) => {
      state.data.establishments = action.payload;
    },
    setDispatchPoints: (state, action: PayloadAction<DispatchPoint[]>) => {
      state.data.dispatchPoints = action.payload;
    },
    setStamps: (state, action: PayloadAction<Stamp[]>) => {
      state.data.stamps = action.payload;
    },
    setTaxPayerParameters: (
      state,
      action: PayloadAction<{
        establishments: Establishment[];
        dispatchPoints: DispatchPoint[];
        stamps: Stamp[];
        connectivityAccess: boolean | null;
        accountingAccess: boolean | null;
        aiAccess: boolean | null;
        aiEnabled: boolean | null;
        aiSettingsManageable: boolean;
        isDemo: boolean | null;
        whatsappEnabled?: boolean;
      }>,
    ) => {
      state.loaded = true;
      state.loading = false;
      state.data.establishments = action.payload.establishments;
      state.data.dispatchPoints = action.payload.dispatchPoints;
      state.data.stamps = action.payload.stamps;
      state.data.connectivityAccess = action.payload.connectivityAccess;
      state.data.aiAccess = action.payload.aiAccess;
      state.data.aiEnabled = action.payload.aiEnabled;
      state.data.aiSettingsManageable = action.payload.aiSettingsManageable;
      state.data.isDemo = action.payload.isDemo;
      state.data.accountingAccess = action.payload.accountingAccess;
      state.data.whatsappEnabled = action.payload.whatsappEnabled === true;
    },
    setTaxPayerParametersError: (state, action: PayloadAction<boolean>) => {
      state.error = action.payload;
    },
    setTaxPayerParametersErrorDetails: (
      state,
      action: PayloadAction<string[]>,
    ) => {
      state.errorDetails = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(LOG_OUT, () => initialState);
    builder.addCase(setTaxPayer, (state, action) => {
      if (state.taxPayerId !== action.payload.id)
        return {
          ...initialState,
          loading: true,
          taxPayerId: action.payload.id,
        };
    });
  },
});

export const {
  setTaxPayerParametersLoaded,
  setTaxPayerParametersLoading,
  setEstablishments,
  setDispatchPoints,
  setStamps,
  setTaxPayerParameters,
  setTaxPayerParametersError,
  setTaxPayerParametersErrorDetails,
} = taxPayerParametersSlice.actions;

export default taxPayerParametersSlice.reducer;
